import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import { useNavigation, useTheme, useRoute } from '@react-navigation/native';
import { DeleteModal, Icon } from '@components';
import { Theme } from '@theme';
import { getCommonStyles } from '@utils';
import ContactsListEdit from '../components/ContactsListEdit';
import { EditInput } from '../components/EditInput';
import { useDeleteHashtag, useGetHashtagById, useScreen, useUpdateHashtag } from '@hooks';
import CloseButton from '../components/CloseButton';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useLanguage } from '@context';

type EditHashtagProps = {
  hashtagId?: string;
  onBack?: () => void;
  onDeleted?: () => void;
};

export const EditHashtag: React.FC<EditHashtagProps> = ({ hashtagId: propHashtagId, onBack, onDeleted }) => {
  const navigation = useNavigation<any>();
  const route = useRoute<any>();
  const theme = useTheme();
  const { t } = useLanguage();
  const {isMobile} = useScreen();
  const styles = getStyle(theme);
  const commonStyles = getCommonStyles(theme);

  const { hashtagId: routeHashtagId } = route.params || {};
  const hashtagId = propHashtagId || routeHashtagId;
  const { data: hashtag, isFetched } = useGetHashtagById(hashtagId);
  const { mutate: updateHashtag, isPending: isSaving } = useUpdateHashtag();
  const { mutate: deleteHashtag, isPending: isDeleting } = useDeleteHashtag();
  const [hashtagName, setHashtagName] = useState('');
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
  
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [hasModified, setHasModified] = useState(false);
  
  useEffect(() => {
    if (hashtag) {
      setHashtagName(hashtag.name);
      setSelectedContactIds(hashtag.contactAccountIds || []);
    } else {
      setHashtagName('');
      setSelectedContactIds([]);
    }
  }, [hashtag, hashtagId]);

  useEffect(() => {
    setHasModified(hashtagName !== hashtag?.name || selectedContactIds !== hashtag?.contactAccountIds);
  }, [hashtagName, selectedContactIds, hashtag]);

  if (!hashtag) {
    return (
      <SafeAreaView style={styles.container}>
        {isMobile && <View style={styles.header}>
          <TouchableOpacity onPress={() => navigation.goBack()}>
            <Icon name="arrowBack" size={25} color={theme.colors.black} />
          </TouchableOpacity>
        </View>}
        <View style={styles.errorContainer}>
          {/* Without an id nothing is asked to the server: there is nothing to wait for. */}
          {hashtagId && !isFetched && <ActivityIndicator size="large" color={theme.colors.primary} />}
          {(isFetched || !hashtagId) && <Text style={styles.errorText}>{t('hashtagsScreen.notFound')}</Text>}
        </View>
      </SafeAreaView>
    );
  }

  const handleBack = () => {
    if(onBack) {
      onBack();
    } else {  
      navigation.goBack();
    }
  };

  const handleSave = () => {
    if (hashtagName.trim()) {
      const cleanTag = hashtagName.trim().replace('#', '')
      // The screen closes once the server has accepted the save: a refused
      // one leaves it open with what was typed.
      updateHashtag({
        id: hashtagId,
        name: '#' + cleanTag,
        contactAccountIds: selectedContactIds,
      }, {
        onSuccess: () => {
          setHasModified(false);
          handleBack();
        },
      });
    }
  };

  const showDeleteModal = () => {
    setIsDeleteModalVisible(true)
  }

  const handleDelete = () => {
      setIsDeleteModalVisible(false);
      deleteHashtag({id: hashtagId}, {
        // Once the server has deleted it; a refused deletion leaves the screen open.
        onSuccess: () => {
          // In the two-pane web layout, clear the selected id in the parent so the
          // detail panel resets and no GET /hashtags/:id fires for the deleted id.
          if (onDeleted) {
            onDeleted();
          } else {
            navigation.reset({
              index: 0,
              routes: [{ name: 'HashtagsList' }],
            });
          }
        },
      });
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        {isMobile && <TouchableOpacity onPress={handleBack}>
          <Icon name="arrowBack" size={25} color={theme.colors.black} />
        </TouchableOpacity>}
        <Text style={styles.headerTitle}>{isMobile? hashtag.name : t('hashtagForm.editTitle')}</Text>
        <TouchableOpacity onPress={showDeleteModal} disabled={isDeleting}>
          <Icon name="trash" size={20} color={theme.colors.error} />
        </TouchableOpacity>
      </View>

      <View style={styles.content}>
        {/* Nom du hashtag */}
        <View style={!isMobile && commonStyles.row}>
          <EditInput placeholder={t('hashtagForm.namePlaceholder')} value={hashtagName} onChangeText={setHashtagName} />
        </View>

        <ContactsListEdit isEdit={selectedContactIds.length > 0} selectedContactIds={selectedContactIds} setSelectedContactIds={setSelectedContactIds}/>
        
        {/* Bouton Sauvegarder */}
        <View style={[styles.footer, !isMobile && commonStyles.rowBetween]}>
          {!isMobile && <CloseButton onPress={handleBack} />}
          <TouchableOpacity
            style={[styles.saveButton, (!hashtagName.trim() || !hasModified || isSaving || isDeleting) && styles.saveButtonDisabled]}
            onPress={handleSave}
            disabled={!hashtagName.trim() || !hasModified || isSaving || isDeleting}
          >
            <Text style={styles.saveButtonText}>
              {t('common.save')}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
      <DeleteModal isVisible={isDeleteModalVisible} setIsVisible={setIsDeleteModalVisible} title={t('editHashtag.deleteConfirm')} onDelete={handleDelete} />
    </SafeAreaView>
  );
};

const getStyle = (theme: Theme) =>
  StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingBottom: theme.spacing.md,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.lightGrey,
  },
  placeholder: {
    width: 30,
  },
  headerTitle: {
    fontSize: theme.fontSize.large,
    fontWeight: 'bold',
    color: theme.colors.text,
  },
  content: {
    flex: 1,
    padding: theme.spacing.md,
  },
  selectedContactItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    borderRadius: 12,
    padding: 12,
    marginBottom: 8,
  },
  selectedContactAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    marginRight: 12,
  },
  selectedContactName: {
    flex: 1,
    fontSize: 16,
    color: theme.colors.text,
  },
  removeButton: {
    padding: 4,
  },
  footer: {
    backgroundColor: theme.colors.white,
  },
  saveButton: {
    borderRadius: 25,
    paddingVertical: theme.spacing.sm,
    paddingHorizontal: theme.spacing.xxxl,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: theme.colors.primary,
  },
  saveButtonDisabled: {
    opacity: 0.5,
  },
  saveButtonText: {
    fontSize: theme.fontSize.small,
    color: theme.colors.primary,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    justifyContent: 'flex-end',
  },
  modalContainer: {
    backgroundColor: theme.colors.white,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    minHeight: 200,
    maxHeight: '80%',
  },
  handle: {
    width: 36,
    height: 4,
    backgroundColor: theme.colors.lightGrey,
    borderRadius: 2,
    alignSelf: 'center',
    marginTop: 8,
    marginBottom: 8,
  },
  modalHeader: {
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.lightGrey,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: theme.colors.text,
    textAlign: 'center',
  },
  modalContactsList: {
    flex: 1,
  },
  contactItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.lightGrey,
  },
  contactAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    marginRight: 12,
  },
  contactName: {
    flex: 1,
    fontSize: 16,
    color: theme.colors.text,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: theme.colors.lightGrey,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.white,
  },
  checkboxSelected: {
    backgroundColor: theme.colors.primary,
    borderColor: theme.colors.primary,
  },
  selectionBar: {
    height: 40,
    width: 6,
    borderRadius: 3,
    backgroundColor: 'transparent',
    marginLeft: theme.spacing.md,
  },
  selectionBarActive: {
    backgroundColor: theme.colors.primary,
  },
  overlayTouchable: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  modalFooter: {
    padding: 20,
    borderTopWidth: 0.5,
    borderTopColor: theme.colors.lightGrey,
  },
  modalSaveButton: {
    backgroundColor: theme.colors.primary,
    borderRadius: 25,
    paddingVertical: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalSaveButtonText: {
    fontSize: 17,
    fontWeight: '600',
    color: theme.colors.white,
  },
  safeArea: {
    flex: 1,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    fontSize: 16,
    color: '#8E8E93',
  },
});

export default EditHashtag; 